// The live card above a web answer (busy-ai-srv#36): `{ type, data }`, sent by ai-srv on its own
// SSE `widget` event as soon as the provider answered, and again on `done` and on the one-shot
// response. This is the one gate every transport goes through, shared by the research store and the
// cards: a card that cannot be drawn honestly is no card at all — the answer always renders without
// it, which is the spec's "every widget fails soft".

import { num } from '@/lib/widgets/format'

export const WIDGET_TYPES = ['weather', 'fx', 'stock', 'sports', 'flights', 'hotels', 'events']

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const hasItems = (v) => Array.isArray(v) && v.length > 0

// What each type needs before it is worth drawing. Everything else on a card is optional, and the
// card leaves out the line it has nothing for.
const USABLE = {
  weather: (d) => isObj(d.current) && num(d.current.tempC) !== null,
  fx: (d) => num(d.rate) !== null && num(d.converted) !== null && !!d.from && !!d.to,
  stock: (d) => num(d.price) !== null && !!d.symbol,
  sports: (d) => isObj(d.team) && !!d.team.name && (isObj(d.next) || hasItems(d.last) || hasItems(d.table)),
  flights: (d) => hasItems(d.offers),
  hotels: (d) => hasItems(d.offers),
  events: (d) => hasItems(d.events),
}

/**
 * The wire's widget → `{ type, data }`, or null when there is none to draw (absent, an unknown type,
 * a data object missing what the card needs). Read under both casings the wire may use.
 */
export function toWidget(raw) {
  if (!isObj(raw)) return null
  const type = String(raw.type ?? raw.Type ?? '').trim().toLowerCase()
  const data = raw.data ?? raw.Data
  if (!WIDGET_TYPES.includes(type) || !isObj(data)) return null
  return USABLE[type](data) ? { type, data } : null
}
